import React, { useState, useEffect } from 'react';
import './Terms.css';

const Terms = () => {
  const [activeTab, setActiveTab] = useState('terms');

  const hashToTabMap = {
    terms: 'terms',
    'privacy-policy': 'privacy',
    'platform-agreement': 'platform',
    'offer-terms': 'offer',
    'prohibited-activities': 'prohibited',
    'refund-policy': 'refund',
  };

  const tabToHashMap = {
    terms: 'terms',
    privacy: 'privacy-policy',
    platform: 'platform-agreement',
    offer: 'offer-terms',
    prohibited: 'prohibited-activities',
    refund: 'refund-policy',
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const hash = window.location.hash.replace('#', '');
    const tabKey = hashToTabMap[hash];
    if (tabKey) {
      setActiveTab(tabKey);
    }
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function to handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const hash = tabToHashMap[tab] || 'terms';
    window.location.hash = hash;
  };

  // Placeholder content for each section
  const termsContent = (
    <>
      <h2 className="fw-bold mb-4">Terms & Conditions</h2>
      <p>
        Welcome to AI Chat Assist. These terms and conditions govern your use of our platform and
        services. Please read them carefully before using our services.
      </p>

      <h3 className="fw-bold mb-3 mt-4">1. Acceptance of Terms</h3>
      <p>
        By accessing or using AI Chat Assist, you agree to comply with and be bound by these terms.
        If you do not agree with any part of these terms, you may not use our services.
      </p>

      <h3 className="fw-bold mb-3 mt-4">2. Use of Services</h3>
      <p>
        AI Chat Assist provides AI-driven communication services. Users are responsible for their
        interactions, and any misuse or violation of ethical standards may result in the termination
        of service access.
      </p>

      <h3 className="fw-bold mb-3 mt-4">3. User Responsibilities</h3>
      <p>
        Users are responsible for the security of their accounts and agree not to share login
        credentials. AI Chat Assist is not liable for any unauthorized access resulting from the
        user's failure to safeguard their account information.
      </p>

      <h3 className="fw-bold mb-3 mt-4">4. Data Privacy</h3>
      <p>
        AI Chat Assist respects user privacy. While the platform collects necessary data for service
        improvement, we are committed to protecting user information and adhering to privacy
        regulations. Please refer to our Privacy Policy for more details.
      </p>

      <h3 className="fw-bold mb-3 mt-4">5. Intellectual Property</h3>
      <p>
        All content, logos, and materials on AI Chat Assist are the intellectual property of the
        platform. Users agree not to reproduce, modify, or distribute any content without explicit
        permission.
      </p>

      <h3 className="fw-bold mb-3 mt-4">6. Limitation of Liability</h3>
      <p>
        AI Chat Assist strives for uninterrupted service, but we do not guarantee the platform's
        continuous availability. Users agree that the platform is provided "as-is" without any
        warranty, and AI Chat Assist is not liable for any consequential damages.
      </p>

      <h3 className="fw-bold mb-3 mt-4">7. Termination of Services</h3>
      <p>
        AI Chat Assist reserves the right to terminate services at its discretion, especially in
        cases of misuse, unethical conduct, or violation of these terms.
      </p>

      <h3 className="fw-bold mb-3 mt-4">8. Updates to Terms</h3>
      <p>
        AI Chat Assist may update these terms periodically. Users will be notified of any changes,
        and continued use of the services constitutes acceptance of the updated terms.
      </p>
    </>
  );

  const privacyContent = (
    <>
      <h2 className="fw-bold mb-4">Privacy Policy</h2>
      <p>
        At AI Chat Assist, we are committed to protecting your privacy. This privacy policy outlines
        how we collect, use and safeguard your personal information. By using our services, you
        agree to the terms outlined in this policy.
      </p>

      <h3 className="fw-bold mb-3 mt-4">1. Information Collection</h3>
      <p>
        We collect information you provide during account registration and interactions with AI Chat
        Assist. This may include personal details, communication history, and usage patterns.
      </p>

      <h3 className="fw-bold mb-3 mt-4">2. Data Usage</h3>
      <p>
        AI Chat Assist uses collected data to improve service quality, personalize user experiences,
        and provide support. Your information will not be shared with third parties without your
        consent, except as required by law.
      </p>

      <h3 className="fw-bold mb-3 mt-4">3. Security Measures</h3>
      <p>
        We implement industry-standard security measures to protect your data from unauthorized
        access, alteration, or disclosure. However, no online platform can guarantee absolute
        security, and users are encouraged to adopt strong password practices.
      </p>

      <h3 className="fw-bold mb-3 mt-4">4. Cookies and Tracking</h3>
      <p>
        AI Chat Assist may use cookies and similar technologies to enhance user experience and
        collect usage data. Users can manage cookie preferences through their browser settings.
      </p>

      <h3 className="fw-bold mb-3 mt-4">5. Third-Party Services</h3>
      <p>
        AI Chat Assist may integrate with third-party services for specific functionalities. Users
        are encouraged to review the privacy policies of these services as AI Chat Assist is not
        responsible for their practices.
      </p>

      <h3 className="fw-bold mb-3 mt-4">6. Children's Privacy</h3>
      <p>
        AI Chat Assist is not intended for children under the age of 13. We do not knowingly collect
        personal information from children, and parents or guardians should contact us if they
        believe their child's information has been submitted.
      </p>

      <h3 className="fw-bold mb-3 mt-4">7. Data Retention</h3>
      <p>
        We retain user data as long as necessary for the provision of services and as required by
        law. Users can request data deletion, subject to legal constraints.
      </p>

      <h3 className="fw-bold mb-3 mt-4">8. Policy Updates</h3>
      <p>
        AI Chat Assist may update this privacy policy periodically. Users will be notified of any
        changes, and continued use of our services constitutes acceptance of the updated policy.
      </p>

      <h3 className="fw-bold mb-3 mt-4">9. Contact Information</h3>
      <p>
        For questions, concerns, or requests related to privacy, please contact our support team at{' '}
        <a href="mailto:support@aichatassist.com" className="support-email">
          <strong>support@aichatassist.com</strong>
        </a>
        . By using AI Chat Assist, you acknowledge and consent to the practices outlined in this
        privacy policy. Thank you for entrusting us with your privacy.
      </p>
    </>
  );

  const platformContent = (
    <>
      <h2 className="fw-bold mb-4">Platform Agreement</h2>
      <p>
        Welcome to AI Chat Assist! This Platform Agreement outlines the terms and conditions
        governing your use of our services. By accessing or using AI Chat Assist, you agree to
        comply with and be bound by this agreement. If you do not agree with any part of these
        terms, please refrain from using our platform.
      </p>

      <h3 className="fw-bold mb-3 mt-4">1. Acceptance of Agreement</h3>
      <p>
        By accessing or using AI Chat Assist, you acknowledge that you have read, understood, and
        agreed to the terms and conditions outlined in this Platform Agreement.
      </p>

      <h3 className="fw-bold mb-3 mt-4">2. Service Access</h3>
      <p>
        AI Chat Assist provides AI-driven communication services. Users are granted access to the
        platform's features, subject to compliance with this agreement.
      </p>

      <h3 className="fw-bold mb-3 mt-4">3. User Responsibilities</h3>
      <p>
        Users are responsible for maintaining the confidentiality of their login credentials.
        Unauthorized access due to negligence may result in the suspension of service access.
      </p>

      <h3 className="fw-bold mb-3 mt-4">4. Ethical Use</h3>
      <p>
        Users agree to use AI Chat Assist ethically and responsibly. Misuse, abuse, or any violation
        of ethical standards may result in the termination of service access.
      </p>

      <h3 className="fw-bold mb-3 mt-4">5. User Content</h3>
      <p>
        Users retain ownership of content they submit to AI Chat Assist. By providing content, users
        grant AI Chat Assist a non-exclusive license to use, modify, and display the content for the
        purpose of providing services.
      </p>

      <h3 className="fw-bold mb-3 mt-4">6. Intellectual Property</h3>
      <p>
        All content, logos, and materials on AI Chat Assist are the intellectual property of the
        platform. Users agree not to reproduce, modify, or distribute any content without explicit
        permission.
      </p>

      <h3 className="fw-bold mb-3 mt-4">7. Updates and Modifications</h3>
      <p>
        AI Chat Assist may update and modify platform features and functionalities. Users will be
        notified of significant changes, and continued use of the platform constitutes acceptance of
        these updates.
      </p>

      <h3 className="fw-bold mb-3 mt-4">8. Termination of Services</h3>
      <p>
        AI Chat Assist reserves the right to terminate services at its discretion, especially in
        cases of misuse, unethical conduct, or violation of this agreement.
      </p>

      <h3 className="fw-bold mb-3 mt-4">9. Liability Limitations</h3>
      <p>
        AI Chat Assist is not liable for any indirect, consequential, or incidental damages. The
        platform is provided "as-is" without any warranty, and users use the platform at their own
        risk.
      </p>

      <h3 className="fw-bold mb-3 mt-4">10. Contact Information</h3>
      <p>
        For questions, concerns, or requests related to this agreement, please contact our support
        team at{' '}
        <a href="mailto:support@aichatassist.com" className="support-email">
          <strong>support@aichatassist.com</strong>
        </a>
        . Thank you for choosing AI Chat Assist!
      </p>
    </>
  );

  const offerContent = (
    <>
      <h2 className="fw-bold mb-4">Offer Terms</h2>
      <p>
        These terms and conditions govern offers, promotions, and discounts provided by AI Chat
        Assist. By participating in any offer, you agree to comply with and be bound by these terms.
      </p>

      <h3 className="fw-bold mb-3 mt-4">1. Eligibility</h3>
      <p>
        Offers are available to eligible users based on criteria determined by AI Chat Assist. Users
        must meet specified requirements to qualify for promotions or discounts.
      </p>

      <h3 className="fw-bold mb-3 mt-4">2. Duration</h3>
      <p>
        Offers are valid for a specified duration and must be redeemed within the stated timeframe.
        AI Chat Assist reserves the right to modify or withdraw offers at its discretion.
      </p>

      <h3 className="fw-bold mb-3 mt-4">3. Redemption Guidelines</h3>
      <p>
        Users must adhere to the specified guidelines for offer redemption. Failure to comply with
        these guidelines may result in the forfeiture of the offer.
      </p>

      <h3 className="fw-bold mb-3 mt-4">4. Non-Transferable</h3>
      <p>
        Offers are non-transferable and may only be used by the intended recipient. Users cannot
        transfer or assign offers to other accounts or individuals.
      </p>

      <h3 className="fw-bold mb-3 mt-4">5. Modification or Termination</h3>
      <p>
        AI Chat Assist reserves the right to modify or terminate offers at any time without prior
        notice. Users will be notified of any changes, and continued use of offers constitutes
        acceptance of modified terms.
      </p>

      <h3 className="fw-bold mb-3 mt-4">6. Acknowledgment</h3>
      <p>
        By proceeding with AI Chat Assist offers, you acknowledge that you have read, understood,
        and agreed to these offer terms. If you have any questions or concerns, please contact our
        support team at{' '}
        <a href="mailto:support@aichatassist.com" className="support-email">
          <strong>support@aichatassist.com</strong>
        </a>
        . Thank you for choosing AI Chat Assist!
      </p>
    </>
  );

  const prohibitedContent = (
    <>
      <h2 className="fw-bold mb-4">Prohibited Activities</h2>
      <p>
        To ensure a safe and positive environment for all users, the following activities are
        strictly prohibited on AI Chat Assist. Violation of these guidelines may result in the
        termination of your account or access to our services.
      </p>

      <h3 className="fw-bold mb-3 mt-4">1. Misuse of AI Chat Assist</h3>
      <p>
        Users must refrain from any misuse, abuse, or unauthorized access to AI Chat Assist. Any
        activity that compromises the integrity or functionality of the platform is strictly
        prohibited.
      </p>

      <h3 className="fw-bold mb-3 mt-4">2. Unethical Conduct</h3>
      <p>
        Users are expected to engage with AI Chat Assist ethically. Any unethical conduct, including
        the dissemination of false information, harassment, or any form of malicious activity, is
        strictly forbidden.
      </p>

      <h3 className="fw-bold mb-3 mt-4">3. Violation of Intellectual Property</h3>
      <p>
        Users must respect intellectual property rights. Unauthorized reproduction, modification, or
        distribution of content, logos, or materials on AI Chat Assist is prohibited.
      </p>

      <h3 className="fw-bold mb-3 mt-4">4. Unauthorized Access</h3>
      <p>
        Unauthorized access to accounts, systems, or data on AI Chat Assist is strictly prohibited.
        Users are responsible for maintaining the confidentiality of their login credentials.
      </p>

      <h3 className="fw-bold mb-3 mt-4">5. Violation of Legal Standards</h3>
      <p>
        Users must adhere to all applicable laws and regulations while using AI Chat Assist. Any
        activity that violates legal standards, including but not limited to privacy laws, is
        strictly prohibited.
      </p>

      <h3 className="fw-bold mb-3 mt-4">6. Consequences</h3>
      <p>
        Engaging in prohibited activities may result in the termination of user accounts or access
        to AI Chat Assist. AI Chat Assist reserves the right to investigate and take appropriate
        action against individuals found in violation of these guidelines.
      </p>

      <h3 className="fw-bold mb-3 mt-4">7. Contact Information</h3>
      <p>
        If you have any questions or concerns regarding prohibited activities, please contact our
        support team at{' '}
        <a href="mailto:support@aichatassist.com" className="support-email">
          <strong>support@aichatassist.com</strong>
        </a>
        . Thank you for your cooperation in creating a safe and positive environment on AI Chat
        Assist!
      </p>
    </>
  );

  const refundContent = (
    <>
      <h2 className="fw-bold mb-4">Refund Policy</h2>
      <p>
        At AI Chat Assist, we strive to deliver the best AI-powered chatbot services to our
        customers. However, if you are not satisfied with your purchase, we're here to help.
      </p>

      <h3 className="fw-bold mb-3 mt-4">1. Requesting a Refund</h3>
      <p>
        To initiate a refund, please contact our support team exclusively via email at:{' '}
        <a href="mailto:support@aichatassist.com" className="support-email">
          <strong>support@aichatassist.com</strong>
        </a>
        . All refund-related communications and processing will be handled only through email for
        proper documentation and support tracking.
      </p>

      <h3 className="fw-bold mb-3 mt-4">2. Refund Processing Time</h3>
      <p>
        Once a refund request is received and approved, the processing may take up to 18-20 business
        days. Please note that timelines may vary based on your payment provider or banking
        institution.
      </p>

      <h3 className="fw-bold mb-3 mt-4">3. Refund Type</h3>
      <p>
        Refunds may be full or partial, depending on the nature of the case and the services used.
        Each request will be carefully evaluated before the refund amount is finalized.
      </p>

      <h3 className="fw-bold mb-3 mt-4">4. Important Notes</h3>
      <ul>
        <li>Refund requests must include a valid reason and any relevant supporting details.</li>
        <li>
          We reserve the right to assess each refund request individually based on the nature of the
          issue.
        </li>
        <li>
          Refunds are applicable only under specific circumstances such as service failure, double
          payments, or unauthorized transactions.
        </li>
      </ul>

      <h3 className="fw-bold mb-3 mt-4">5. Need Help?</h3>
      <p>
        For any issues, concerns, or follow-ups related to refunds, please reach out only via email
        at{' '}
        <a href="mailto:support@aichatassist.com" className="support-email">
          <strong>support@aichatassist.com</strong>
        </a>
        . Our support team will do their best to resolve your concern promptly and fairly.
      </p>
    </>
  );

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'terms':
        return termsContent;
      case 'privacy':
        return privacyContent;
      case 'platform':
        return platformContent;
      case 'offer':
        return offerContent;
      case 'prohibited':
        return prohibitedContent;
      case 'refund':
        return refundContent;
      default:
        return termsContent;
    }
  };

  return (
    <div className="terms-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h1>Legal Information</h1>
              <p className="text-muted">Last Updated: May 20, 2025</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="terms-container">
        {/* Sidebar Navigation */}
        <div className="terms-sidebar">
          <div className="terms-nav">
            <div className="nav-item">
              <button
                className={`nav-link ${activeTab === 'terms' ? 'active' : ''}`}
                onClick={() => handleTabChange('terms')}
              >
                Terms & Conditions
              </button>
            </div>
            <div className="nav-item">
              <button
                className={`nav-link ${activeTab === 'privacy' ? 'active' : ''}`}
                onClick={() => handleTabChange('privacy')}
              >
                Privacy Policy
              </button>
            </div>
            <div className="nav-item">
              <button
                className={`nav-link ${activeTab === 'platform' ? 'active' : ''}`}
                onClick={() => handleTabChange('platform')}
              >
                Platform Agreement
              </button>
            </div>
            <div className="nav-item">
              <button
                className={`nav-link ${activeTab === 'offer' ? 'active' : ''}`}
                onClick={() => handleTabChange('offer')}
              >
                Offer Terms
              </button>
            </div>
            <div className="nav-item">
              <button
                className={`nav-link ${activeTab === 'prohibited' ? 'active' : ''}`}
                onClick={() => handleTabChange('prohibited')}
              >
                Prohibited Activities
              </button>
            </div>
            <div className="nav-item">
              <button
                className={`nav-link ${activeTab === 'refund' ? 'active' : ''}`}
                onClick={() => handleTabChange('refund')}
              >
                Refund Policy
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="terms-content-container">
          <div className="terms-content">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
